/*
  TrendDrop — product page extras (COMMERCE).
  - Re-broadcasts the core variant change as `variant:changed` on document ({ variant, sectionId }).
  - Back-in-stock block: shows for sold-out variants and carries the variant id.
  - <td-sticky-atc>: frosted bar shown once the main add-to-cart scrolls away; mirrors price/button.
  - Desktop thumbnail rail: smooth-scrolls to stacked media, tracks the active item, follows the gallery order.
  - Gallery cap: the "See all" tile / rail thumb reveals the media hidden after the cap (CSS in main-product).
  - Mobile gallery dots: up to 7 slides show dots instead of "1/7" (the counter stays for screen readers).
  - The sticky bar exposes --td-sticky-atc-h on :root while it is visible (dock / page padding use it).
  - Copy buttons use FOUNDATION's global [data-td-copy] handler.
*/
(() => {
  if (window.__tdProductInit) return;
  window.__tdProductInit = true;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- variant:changed + back in stock ---------- */
  const onVariant = ({ data } = {}) => {
    if (!data) return;
    const { sectionId, variant } = data;
    document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant, sectionId } }));
    const root = document.querySelector(`product-info[data-section="${sectionId}"]`) ||
      document.querySelector(`product-info[data-original-section="${sectionId}"]`);
    if (!root) return;
    root.querySelectorAll('[data-td-bis]').forEach((bis) => {
      bis.hidden = !variant || variant.available;
      const id = bis.querySelector('[data-td-bis-id]');
      const title = bis.querySelector('[data-td-bis-title]');
      if (id && variant) id.value = variant.id;
      if (title && variant) title.value = variant.title;
    });
    document.querySelectorAll(`td-sticky-atc[data-section-id="${sectionId}"] [data-td-sticky-variant]`).forEach((el) => {
      if (variant && variant.title && variant.title !== 'Default Title') el.textContent = variant.title;
      else if (!variant) el.textContent = window.variantStrings?.unavailable || 'Unavailable';
    });
    if (variant && !variant.featured_media && data.html) syncGalleryOrder(root, data.html, sectionId);
    if (data.html) syncRailItems(root, data.html);
    syncRailOrder(root);
    root.querySelectorAll('slider-component[id^="GalleryViewer"]').forEach((viewer) => viewer._tdDotsRender?.());
  };

  /* FEAT-PDP colour-first gallery, fallback: product-info.js only re-sorts the gallery when the new variant has
     a featured media. When it has none (colour matched by alt text only), apply the server order ourselves so
     the selected colour's images still come first, and bring the first one into view. */
  const syncGalleryOrder = (root, html, sectionId) => {
    const list = root.querySelector('media-gallery .product__media-list');
    const next = html.querySelector('media-gallery .product__media-list');
    if (!list || !next) return;
    const order = Array.from(next.querySelectorAll(':scope > li[data-media-id]')).map((li) => li.dataset.mediaId);
    const current = Array.from(list.querySelectorAll(':scope > li[data-media-id]')).map((li) => li.dataset.mediaId);
    if (!order.length || order.join() === current.join()) return;
    const more = list.querySelector(':scope > [data-td-media-more-item]');
    order.forEach((id) => {
      const li = list.querySelector(`:scope > li[data-media-id="${id}"]`);
      if (li) list.insertBefore(li, more);
    });
    const first = list.querySelector(':scope > li[data-media-id]');
    if (!first) return;
    list.querySelectorAll(':scope > li.is-active').forEach((li) => li.classList.remove('is-active'));
    first.classList.add('is-active');
    const viewer = root.querySelector(`#GalleryViewer-${sectionId}`) || root.querySelector('slider-component[id^="GalleryViewer"]');
    if (viewer && typeof viewer.resetPages === 'function') viewer.resetPages();
    if (list.scrollWidth > list.clientWidth + 1) list.scrollTo({ left: 0, behavior: reduceMotion() ? 'auto' : 'smooth' });
  };

  /* product-info.js re-sorts the gallery to the new colour's order; mirror it in the desktop rail. */
  /* Chosen colour only (hide_variants): the server renders a different set of thumbnails per colour, so take the
     rail's items from the re-rendered section when the set changed (clicks are delegated on the rail). */
  const syncRailItems = (root, html) => {
    const list = root.querySelector('.td-thumb-rail__list');
    const next = html.querySelector('.td-thumb-rail__list');
    if (!list || !next) return;
    const ids = (el) => Array.from(el.querySelectorAll('[data-td-thumb]')).map((t) => t.dataset.tdThumb).sort().join();
    if (ids(list) === ids(next)) return;
    list.replaceChildren(...Array.from(next.childNodes).map((n) => document.importNode(n, true)));
  };
  const syncRailOrder = (root) => {
    const list = root.querySelector('.td-thumb-rail__list');
    const gallery = root.querySelector('media-gallery .product__media-list');
    if (!list || !gallery) return;
    const more = list.querySelector('[data-td-media-more-item]');
    gallery.querySelectorAll(':scope > li[data-media-id]').forEach((li) => {
      const item = list.querySelector(`[data-td-thumb="${li.dataset.mediaId}"]`)?.closest('li');
      if (item) list.insertBefore(item, more);
    });
    const first = gallery.querySelector(':scope > li[data-media-id]');
    list.querySelectorAll('[data-td-thumb]').forEach((thumb) => {
      const on = !!first && thumb.dataset.tdThumb === first.dataset.mediaId;
      thumb.classList.toggle('is-active', on);
      if (on) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  };
  const subscribeVariant = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, onVariant);
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', subscribeVariant);
  else subscribeVariant();

  /* ---------- Sticky add to cart ---------- */
  if (!customElements.get('td-sticky-atc')) {
    class TdStickyAtc extends HTMLElement {
      connectedCallback() {
        const id = this.dataset.sectionId;
        this.mainButton = document.getElementById(`ProductSubmitButton-${id}`);
        this.mainPrice = document.getElementById(`price-${id}`);
        this.button = this.querySelector('[data-td-sticky-button]');
        this.label = this.querySelector('[data-td-sticky-label]');
        this.price = this.querySelector('[data-td-sticky-price]');
        // No main buy button or no target form (buy_buttons block removed): never leave a dead bar.
        if (!this.mainButton || !document.getElementById(this.dataset.formId)) {
          this.remove();
          return;
        }

        this.io = new IntersectionObserver(([entry]) => {
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.toggle(past);
        });
        this.io.observe(this.mainButton);

        this.mo = new MutationObserver(() => this.sync());
        this.mo.observe(this.mainButton, { attributes: true, childList: true, subtree: true, characterData: true });
        if (this.mainPrice) this.mo.observe(this.mainPrice, { childList: true, subtree: true, characterData: true });
        this.sync();
      }
      disconnectedCallback() {
        this.io?.disconnect();
        this.mo?.disconnect();
        this.ro?.disconnect();
        document.documentElement.classList.remove('td-has-sticky-atc');
        document.documentElement.style.removeProperty('--td-sticky-atc-h');
      }
      toggle(show) {
        this.visible = show;
        this.classList.toggle('is-visible', show);
        this.toggleAttribute('inert', !show);
        this.setAttribute('aria-hidden', String(!show));
        document.documentElement.classList.toggle('td-has-sticky-atc', show);
        this.setHeightVar();
      }
      /* --td-sticky-atc-h on :root only while the bar is shown (bar height only; spacing is owned elsewhere). */
      setHeightVar() {
        const root = document.documentElement;
        const bar = this.querySelector('.td-sticky-atc__bar');
        if (!this.visible || !bar) {
          root.style.removeProperty('--td-sticky-atc-h');
          return;
        }
        root.style.setProperty('--td-sticky-atc-h', `${Math.round(bar.getBoundingClientRect().height)}px`);
        if (!this.ro && 'ResizeObserver' in window) {
          this.ro = new ResizeObserver(() => {
            if (this.visible) this.setHeightVar();
          });
          this.ro.observe(bar);
        }
      }
      sync() {
        if (!this.button) return;
        const disabled = this.mainButton.hasAttribute('disabled');
        this.button.toggleAttribute('disabled', disabled);
        const text = this.mainButton.querySelector('span:not(.hidden):not(.sold-out-message)')?.textContent.trim();
        if (text && this.label) this.label.textContent = text;
        if (this.mainPrice && this.price) {
          const source = this.mainPrice.querySelector('.price');
          if (source) this.price.innerHTML = source.outerHTML;
        }
      }
    }
    customElements.define('td-sticky-atc', TdStickyAtc);
  }

  /* ---------- Gallery cap: "See all" ---------- */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-td-media-more]');
    if (!btn) return;
    const wrap = btn.closest('.td-product__media');
    if (!wrap || wrap.classList.contains('is-expanded')) return;
    const cap = parseInt(wrap.dataset.tdMediaCap, 10) || 0;
    wrap.classList.add('is-expanded');
    wrap.querySelectorAll('[data-td-media-more]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const viewer = wrap.querySelector('slider-component[id^="GalleryViewer"]');
    if (viewer && typeof viewer.resetPages === 'function') viewer.resetPages();
    viewer?._tdDotsRender?.();
    const slides = wrap.querySelectorAll('.product__media-list > li[data-media-id]');
    const next = slides[cap] || slides[0];
    if (!next) return;
    const list = next.parentElement;
    const behavior = reduceMotion() ? 'auto' : 'smooth';
    if (list && list.scrollWidth > list.clientWidth + 1) list.scrollTo({ left: next.offsetLeft, behavior });
    else next.scrollIntoView({ behavior, block: 'start' });
    next.querySelector('.product__media-toggle')?.focus({ preventScroll: true });
  });

  /* ---------- Mobile gallery dots (up to 7 slides) ---------- */
  const MAX_DOTS = 7;
  const initDots = (scope = document) => {
    scope.querySelectorAll('.td-product slider-component[id^="GalleryViewer"]').forEach((viewer) => {
      if (viewer._tdDotsRender) return;
      const buttons = viewer.querySelector('.slider-buttons');
      const counter = buttons && buttons.querySelector('.slider-counter');
      if (!counter) return;
      const dots = document.createElement('div');
      dots.className = 'td-gallery-dots';
      dots.setAttribute('aria-hidden', 'true');
      counter.after(dots);
      const render = () => {
        const total = viewer.totalPages || 0;
        const useDots = total > 1 && total <= MAX_DOTS;
        buttons.classList.toggle('td-has-dots', useDots);
        if (!useDots) {
          dots.replaceChildren();
          return;
        }
        if (dots.childElementCount !== total) {
          const items = [];
          for (let i = 0; i < total; i += 1) {
            const dot = document.createElement('span');
            dot.className = 'td-gallery-dots__dot';
            items.push(dot);
          }
          dots.replaceChildren(...items);
        }
        const current = Math.min(Math.max((viewer.currentPage || 1) - 1, 0), total - 1);
        Array.from(dots.children).forEach((dot, i) => dot.classList.toggle('is-active', i === current));
      };
      viewer._tdDotsRender = render;
      viewer.addEventListener('slideChanged', render);
      if ('ResizeObserver' in window && viewer.slider) {
        new ResizeObserver(() => requestAnimationFrame(render)).observe(viewer.slider);
      }
      render();
    });
  };
  const startDots = (scope) => {
    if (!window.customElements) return;
    customElements.whenDefined('slider-component').then(() => initDots(scope));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => startDots());
  else startDots();
  document.addEventListener('shopify:section:load', (e) => startDots(e.target));

  /* ---------- Thumbnail rail ---------- */
  const initRails = (scope = document) => {
    scope.querySelectorAll('.td-thumb-rail').forEach((rail) => {
      if (rail._tdInit) return;
      rail._tdInit = true;
      const product = rail.closest('.td-product');
      const setActive = (mediaId) => {
        // Queried live: the rail's items are replaced on colour change (syncRailItems).
        rail.querySelectorAll('[data-td-thumb]').forEach((t) => {
          const on = t.dataset.tdThumb === mediaId;
          t.classList.toggle('is-active', on);
          if (on) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
      };
      rail.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-td-thumb]');
        if (!thumb) return;
        const target = product?.querySelector(`li[data-media-id="${thumb.dataset.tdThumb}"]`);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
        setActive(thumb.dataset.tdThumb);
      });
      if (!('IntersectionObserver' in window) || !product) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) setActive(e.target.dataset.mediaId);
          });
        },
        { rootMargin: '-45% 0px -45% 0px' }
      );
      product.querySelectorAll('.product__media-list li[data-media-id]').forEach((li) => io.observe(li));
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initRails());
  else initRails();
  document.addEventListener('shopify:section:load', (e) => initRails(e.target));

  /* ---------- FEAT-PDP: zoom viewer on phones (swipe + pinch + double-tap) ----------
     The core <product-modal> lightbox shows one item at a time below 750px. This adds: swipe left/right between
     items, two-finger pinch zoom (1–4x) with pan, double-tap to zoom 2.5x / reset, prev/next buttons with an
     "n / total" counter, and arrow keys. Desktop keeps the core stacked viewer. The modal's content is replaced
     on variant change, so slides are always read live from the DOM. */
  const PHONE = window.matchMedia('(max-width: 749px)');
  const CHEVRON = (d) =>
    `<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="${d}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const initZoom = () => {
    document.querySelectorAll('product-modal.product-media-modal').forEach((modal) => {
      if (modal._tdZoom) return;
      modal._tdZoom = true;
      const dialog = modal.querySelector('.product-media-modal__dialog');
      const content = modal.querySelector('.product-media-modal__content');
      if (!dialog || !content) return;
      modal.classList.add('td-zoomable');

      const nav = document.createElement('div');
      nav.className = 'td-zoom-nav';
      nav.innerHTML =
        `<button type="button" class="td-zoom-nav__btn" data-td-zoom-step="-1" aria-label="Previous image">${CHEVRON('M15 18l-6-6 6-6')}</button>` +
        '<span class="td-zoom-nav__count" aria-live="polite"></span>' +
        `<button type="button" class="td-zoom-nav__btn" data-td-zoom-step="1" aria-label="Next image">${CHEVRON('M9 6l6 6-6 6')}</button>` +
        '<span class="td-zoom-nav__hint">Pinch or double-tap to zoom</span>';
      dialog.appendChild(nav);
      const count = nav.querySelector('.td-zoom-nav__count');
      // The core media modal closes on any mouse pointerup; keep the buttons from closing it.
      nav.addEventListener('pointerup', (e) => e.stopPropagation());

      const slides = () => Array.from(content.children).filter((el) => el.hasAttribute('data-media-id'));
      const activeIndex = () => Math.max(0, slides().findIndex((el) => el.classList.contains('active')));
      const activeImg = () => {
        const el = slides()[activeIndex()];
        return el && el.tagName === 'IMG' ? el : null;
      };

      const z = { s: 1, x: 0, y: 0 };
      const apply = (img, animate = false) => {
        if (!img) return;
        img.style.transition = animate && !reduceMotion() ? 'transform .25s var(--td-ease-media, ease)' : '';
        img.style.transform = z.s === 1 && !z.x && !z.y ? '' : `translate3d(${z.x}px, ${z.y}px, 0) scale(${z.s})`;
        modal.classList.toggle('is-zoomed', z.s > 1);
      };
      const reset = (animate = false) => {
        z.s = 1;
        z.x = 0;
        z.y = 0;
        content.querySelectorAll(':scope > img[style]').forEach((img) => {
          img.style.transition = animate && !reduceMotion() ? 'transform .25s var(--td-ease-media, ease)' : '';
          img.style.transform = '';
        });
        modal.classList.remove('is-zoomed');
      };
      const clampPan = (img) => {
        const maxX = Math.max(0, (img.offsetWidth * z.s - img.offsetWidth) / 2);
        const maxY = Math.max(0, (img.offsetHeight * z.s - img.offsetHeight) / 2);
        z.x = Math.min(maxX, Math.max(-maxX, z.x));
        z.y = Math.min(maxY, Math.max(-maxY, z.y));
      };
      const updateCount = () => {
        const total = slides().length;
        nav.hidden = total < 1;
        nav.querySelectorAll('[data-td-zoom-step]').forEach((b) => (b.hidden = total < 2));
        count.textContent = total > 1 ? `${activeIndex() + 1} / ${total}` : '';
      };
      const go = (step) => {
        const list = slides();
        if (list.length < 2) return;
        const i = (activeIndex() + step + list.length) % list.length;
        reset();
        window.pauseAllMedia?.();
        list.forEach((el) => el.classList.remove('active'));
        list[i].classList.add('active');
        updateCount();
      };

      nav.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-td-zoom-step]');
        if (btn) go(parseInt(btn.dataset.tdZoomStep, 10));
      });
      modal.addEventListener('keydown', (e) => {
        if (!PHONE.matches || !modal.hasAttribute('open')) return;
        if (e.key === 'ArrowRight') go(1);
        else if (e.key === 'ArrowLeft') go(-1);
      });
      new MutationObserver(() => {
        reset();
        // Mutation callbacks run as a microtask, after product-modal's show() has marked the opened item .active.
        if (modal.hasAttribute('open')) updateCount();
      }).observe(modal, { attributes: true, attributeFilter: ['open'] });

      const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
      const mid = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });
      // Centre of the image without our transform (scale is around the centre, so only translate moves it).
      const centre = (img) => {
        const r = img.getBoundingClientRect();
        return { x: r.left + r.width / 2 - z.x, y: r.top + r.height / 2 - z.y };
      };
      let g = null;
      let lastTap = null;

      content.addEventListener(
        'touchstart',
        (e) => {
          if (!PHONE.matches || !modal.hasAttribute('open')) return;
          const img = activeImg();
          if (e.touches.length === 2 && img) {
            const m = mid(e.touches);
            const c = centre(img);
            g = { type: 'pinch', img, d0: dist(e.touches), s0: z.s, lx: (m.x - c.x - z.x) / z.s, ly: (m.y - c.y - z.y) / z.s, c };
            lastTap = null;
            return;
          }
          if (e.touches.length !== 1) return;
          const p = e.touches[0];
          const now = Date.now();
          if (img && lastTap && now - lastTap.t < 300 && Math.hypot(p.clientX - lastTap.x, p.clientY - lastTap.y) < 30) {
            e.preventDefault();
            if (z.s > 1) reset(true);
            else {
              const c = centre(img);
              z.s = 2.5;
              z.x = (p.clientX - c.x) * (1 - z.s);
              z.y = (p.clientY - c.y) * (1 - z.s);
              clampPan(img);
              apply(img, true);
            }
            lastTap = null;
            g = null;
            return;
          }
          lastTap = { t: now, x: p.clientX, y: p.clientY };
          g = { type: z.s > 1 && img ? 'pan' : 'swipe', img, sx: p.clientX, sy: p.clientY, x0: z.x, y0: z.y };
        },
        { passive: false }
      );

      content.addEventListener(
        'touchmove',
        (e) => {
          if (!g) return;
          if (g.type === 'pinch' && e.touches.length === 2) {
            e.preventDefault();
            const m = mid(e.touches);
            z.s = Math.min(4, Math.max(1, (g.s0 * dist(e.touches)) / g.d0));
            z.x = m.x - g.c.x - g.lx * z.s;
            z.y = m.y - g.c.y - g.ly * z.s;
            clampPan(g.img);
            apply(g.img);
          } else if (g.type === 'pan' && e.touches.length === 1) {
            e.preventDefault();
            z.x = g.x0 + (e.touches[0].clientX - g.sx);
            z.y = g.y0 + (e.touches[0].clientY - g.sy);
            clampPan(g.img);
            apply(g.img);
          } else if (g.type === 'swipe' && e.touches.length === 1) {
            const dx = e.touches[0].clientX - g.sx;
            const dy = e.touches[0].clientY - g.sy;
            if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
              e.preventDefault();
              if (g.img && !reduceMotion()) g.img.style.transform = `translate3d(${dx * 0.35}px, 0, 0)`;
            }
          }
        },
        { passive: false }
      );

      const end = (e) => {
        if (!g) return;
        if (g.type === 'swipe') {
          const p = e.changedTouches && e.changedTouches[0];
          if (g.img) g.img.style.transform = '';
          if (p) {
            const dx = p.clientX - g.sx;
            const dy = p.clientY - g.sy;
            if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.2) {
              lastTap = null;
              go(dx < 0 ? 1 : -1);
            }
          }
          g = null;
        } else if (g.type === 'pinch') {
          if (z.s < 1.05) reset(true);
          if (e.touches.length === 1 && z.s > 1) {
            const p = e.touches[0];
            g = { type: 'pan', img: g.img, sx: p.clientX, sy: p.clientY, x0: z.x, y0: z.y };
          } else g = null;
        } else if (e.touches.length === 0) g = null;
      };
      content.addEventListener('touchend', end);
      content.addEventListener('touchcancel', end);
      PHONE.addEventListener?.('change', () => reset());
    });
  };

  /* ---------- FEAT-PDP: "Design close-up" tile opens the zoom viewer on that image ---------- */
  document.addEventListener('click', (event) => {
    const tile = event.target.closest('[data-td-closeup]');
    if (!tile) return;
    const root = tile.closest('product-info');
    const sectionId = root?.dataset.section;
    const modal = sectionId && document.getElementById(`ProductModal-${sectionId}`);
    if (!modal || typeof modal.show !== 'function') {
      document.getElementById(`Slide-${tile.dataset.tdCloseup}`)?.scrollIntoView({ block: 'center' });
      return;
    }
    event.preventDefault();
    modal.show(tile); // the tile carries data-media-id, which product-modal uses to pick the item
  });

  /* ---------- FEAT-PDP: "Seen on TikTok" — swap the poster for the official embed on click ---------- */
  document.addEventListener('click', (event) => {
    const poster = event.target.closest('[data-td-tiktok]');
    if (!poster) return;
    const id = poster.dataset.tdTiktok;
    if (!/^\d{6,25}$/.test(id || '')) return;
    const frame = poster.closest('[data-td-tiktok-frame]');
    if (!frame) return;
    event.preventDefault();
    window.pauseAllMedia?.();
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.tiktok.com/embed/v2/${id}`;
    iframe.title = 'TikTok video';
    iframe.allow = 'encrypted-media; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.className = 'td-pdp-tiktok__iframe';
    frame.classList.add('is-playing');
    frame.replaceChildren(iframe);
    iframe.focus({ preventScroll: true });
  });

  /* ---------- FEAT-PDP: "Complete the look" — main item + ticked add-ons in ONE /cart/add.js call ----------
     Capture-phase submit listener on the main product form (product-form-<section>): with no add-on ticked it
     does nothing and the core product-form.js handles the add as usual. With add-ons ticked it posts
     { items: [main, ...add-ons], sections, sections_url } as JSON, then repaints and opens the cart drawer
     exactly like product-form.js (publish cartUpdate source 'product-form' → CartDrawer.renderContents). If the
     combined add fails (e.g. an add-on just sold out), it falls back to the normal single add so the main item
     still lands, and says so under the list. */
  const lookFor = (form) => {
    // getAttribute: the product form has an input named "id", so form.id is that input, not the form's id.
    const formId = form instanceof HTMLFormElement ? form.getAttribute('id') || '' : '';
    if (!formId.startsWith('product-form-') || formId.startsWith('product-form-installment-')) return null;
    const sectionId = formId.slice('product-form-'.length);
    return document.querySelector(`[data-td-look][data-section-id="${sectionId}"]`);
  };
  const setLookNote = (look, text) => {
    const note = look.querySelector('[data-td-look-note]');
    if (!note) return;
    if (!note.dataset.default) note.dataset.default = note.textContent;
    note.textContent = text || note.dataset.default;
    note.classList.toggle('is-error', !!text);
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const look = lookFor(form);
      if (!look) return;
      const extras = Array.from(look.querySelectorAll('input[data-td-look-item]:checked:not(:disabled)'));
      const productForm = form.closest('product-form');
      if (!extras.length || !productForm || !productForm.submitButton) return;
      if (typeof fetchConfig !== 'function' || typeof publish !== 'function') return;
      event.preventDefault();
      event.stopPropagation();
      addWithExtras({ event, form, look, extras, productForm });
    },
    true
  );

  const addWithExtras = ({ event, form, look, extras, productForm }) => {
    const button = productForm.submitButton;
    if (button.getAttribute('aria-disabled') === 'true') return;
    const spinner = productForm.querySelector('.loading__spinner');
    productForm.handleErrorMessage?.();
    setLookNote(look, '');
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    spinner?.classList.remove('hidden');

    const fd = new FormData(form);
    const mainId = Number(fd.get('id'));
    const quantity = parseInt(fd.get('quantity'), 10) || 1;
    const main = { id: mainId, quantity };
    const sellingPlan = fd.get('selling_plan');
    if (sellingPlan) main.selling_plan = sellingPlan;
    const properties = {};
    for (const [key, value] of fd.entries()) {
      const m = key.match(/^properties\[(.+)\]$/);
      if (m && typeof value === 'string' && value !== '') properties[m[1]] = value;
    }
    if (Object.keys(properties).length) main.properties = properties;
    const items = [main, ...extras.map((input) => ({ id: Number(input.value), quantity: 1 }))];

    const cart = productForm.cart;
    const body = { items };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
      cart.setActiveElement?.(document.activeElement);
    }
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify(body);

    // Storefront "cart:lines-update" standard event, same as product-form.js but with every line.
    let deferred = null;
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (CartLinesUpdateEvent) {
      deferred = CartLinesUpdateEvent.createPromise();
      productForm.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: i.id, quantity: i.quantity })),
          promise: deferred.promise,
        })
      );
    }

    const done = () => {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
      spinner?.classList.add('hidden');
      if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
    };

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          // Combined add refused: add the main item the normal way and say the add-on didn't make it.
          deferred?.reject(new Error(response.description || response.message || 'Add failed'));
          done();
          setLookNote(look, "We couldn't add the extra item, so only this one went in your cart.");
          productForm.onSubmitHandler(event);
          return;
        }
        productForm.resolveCartLinesUpdate?.(deferred);
        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }
        const first = (response.items && response.items[0]) || {};
        const state = Object.assign({}, response, { id: first.id || mainId, key: first.key });
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: String(mainId),
          cartData: response,
        });
        extras.forEach((input) => (input.checked = false));
        cart.renderContents(state);
        done();
      })
      .catch((e) => {
        console.error(e);
        deferred?.reject(e);
        done();
        setLookNote(look, "Couldn't reach the cart. Please try again.");
      });
  };

  const bootZoom = () => initZoom();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootZoom);
  else bootZoom();
  document.addEventListener('shopify:section:load', bootZoom);
})();
